import { useState, useEffect, useCallback } from "react";
import { C, S, G } from "../../utils/theme";
import { Badge } from "../../components/UI";

// ── MEMORY CARD GAME ────────────────────────────────────────────────────────
const CARD_EMOJIS = ["🎓","📚","🔬","💻","🧪","📐","🌍","🎯","🏆","⭐","🔭","🧬"];

function MemoryGame() {
  const [cards, setCards] = useState([]);
  const [flipped, setFlipped] = useState([]);
  const [matched, setMatched] = useState([]);
  const [moves, setMoves] = useState(0);
  const [gameWon, setGameWon] = useState(false);
  const [timer, setTimer] = useState(0);
  const [running, setRunning] = useState(false);
  const [bestScore, setBestScore] = useState(() => parseInt(localStorage.getItem("mem_best") || "999"));

  const init = useCallback(() => {
    const pairs = [...CARD_EMOJIS.slice(0, 8), ...CARD_EMOJIS.slice(0, 8)];
    const shuffled = pairs.sort(() => Math.random() - 0.5).map((e, i) => ({ id: i, emoji: e, matched: false }));
    setCards(shuffled); setFlipped([]); setMatched([]); setMoves(0); setGameWon(false); setTimer(0); setRunning(false);
  }, []);

  useEffect(() => { init(); }, []);

  useEffect(() => {
    if (!running) return;
    const t = setInterval(() => setTimer(x => x + 1), 1000);
    return () => clearInterval(t);
  }, [running]);

  const flip = (id) => {
    if (flipped.length === 2 || flipped.includes(id) || matched.includes(id)) return;
    if (!running) setRunning(true);
    const newFlipped = [...flipped, id];
    setFlipped(newFlipped);
    if (newFlipped.length === 2) {
      setMoves(m => m + 1);
      const [a, b] = newFlipped.map(i => cards[i]);
      if (a.emoji === b.emoji) {
        const newMatched = [...matched, ...newFlipped];
        setMatched(newMatched);
        setFlipped([]);
        if (newMatched.length === cards.length) {
          setRunning(false); setGameWon(true);
          if (moves + 1 < bestScore) { setBestScore(moves + 1); localStorage.setItem("mem_best", moves + 1); }
        }
      } else {
        setTimeout(() => setFlipped([]), 900);
      }
    }
  };

  const fmt = s => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

  return (
    <div style={{ maxWidth: 520, margin: "0 auto" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <div style={{ display: "flex", gap: 16 }}>
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 20, fontWeight: 800, color: C.text }}>{moves}</div>
            <div style={{ fontSize: 11, color: C.textMuted }}>MOVES</div>
          </div>
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 20, fontWeight: 800, color: C.navy }}>{fmt(timer)}</div>
            <div style={{ fontSize: 11, color: C.textMuted }}>TIME</div>
          </div>
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 20, fontWeight: 800, color: C.goldDark }}>{matched.length / 2}/8</div>
            <div style={{ fontSize: 11, color: C.textMuted }}>PAIRS</div>
          </div>
          {bestScore < 999 && (
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 20, fontWeight: 800, color: "#10B981" }}>{bestScore}</div>
              <div style={{ fontSize: 11, color: C.textMuted }}>BEST</div>
            </div>
          )}
        </div>
        <button style={S.btn("glass", "sm")} onClick={init}>🔄 Restart</button>
      </div>
      {gameWon && (
        <div style={{ textAlign: "center", padding: "24px", marginBottom: 20, background: C.emeraldLight, borderRadius: 16, border: "1px solid #6EE7B7" }}>
          <div style={{ fontSize: 48, marginBottom: 8 }}>🎉</div>
          <div style={{ fontSize: 20, fontWeight: 800, color: "#059669" }}>You Won!</div>
          <div style={{ fontSize: 14, color: C.textMuted, marginTop: 4 }}>{moves} moves • {fmt(timer)}</div>
          {moves === bestScore && <div style={{ fontSize: 13, color: C.goldDark, marginTop: 6 }}>🏆 New Best Score!</div>}
          <button style={{ ...S.btn("success", "md"), marginTop: 14 }} onClick={init}>Play Again</button>
        </div>
      )}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 10 }}>
        {cards.map((card, i) => {
          const isFlipped = flipped.includes(i) || matched.includes(i);
          const isMatched = matched.includes(i);
          return (
            <div key={card.id} onClick={() => flip(i)}
              style={{ aspectRatio: "1", borderRadius: 14, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 28, cursor: isMatched ? "default" : "pointer", background: isMatched ? C.emeraldLight : isFlipped ? "#DBEAFE" : C.surfaceAlt, border: `2px solid ${isMatched ? "#6EE7B7" : isFlipped ? "#93C5FD" : C.border}`, transition: "all 0.2s ease", transform: isFlipped ? "scale(1.05)" : "scale(1)", boxShadow: isMatched ? "0 2px 8px rgba(16,185,129,0.15)" : "none" }}>
              {isFlipped ? card.emoji : <span style={{ fontSize: 22, opacity: 0.3 }}>❓</span>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── QUICK MATH QUIZ ────────────────────────────────────────────────────────
const OPS = [
  (a, b) => ({ q: `${a} + ${b}`, ans: a + b }),
  (a, b) => ({ q: `${a} × ${b}`, ans: a * b }),
  (a, b) => ({ q: `${Math.max(a, b)} - ${Math.min(a, b)}`, ans: Math.max(a, b) - Math.min(a, b) }),
  (a, b) => ({ q: `${a * b} ÷ ${a}`, ans: b }),
];

function MathQuiz() {
  const [score, setScore] = useState(0);
  const [lives, setLives] = useState(3);
  const [current, setCurrent] = useState(null);
  const [options, setOptions] = useState([]);
  const [feedback, setFeedback] = useState(null);
  const [gameOver, setGameOver] = useState(false);
  const [streak, setStreak] = useState(0);
  const [bestStreak, setBestStreak] = useState(() => parseInt(localStorage.getItem("math_best") || "0"));
  const [level, setLevel] = useState(1);

  const gen = useCallback(() => {
    const max = Math.min(9, 3 + level);
    const a = Math.floor(Math.random() * max) + 1;
    const b = Math.floor(Math.random() * max) + 1;
    const opFn = OPS[Math.floor(Math.random() * (level < 2 ? 2 : OPS.length))];
    const { q, ans } = opFn(a, b);
    const wrong = new Set([ans]);
    while (wrong.size < 4) { const w = ans + Math.floor(Math.random() * 11) - 5; if (w !== ans && w >= 0) wrong.add(w); }
    const opts = [...wrong].sort(() => Math.random() - 0.5);
    setCurrent({ q, ans }); setOptions(opts); setFeedback(null);
  }, [level]);

  useEffect(() => { if (!gameOver) gen(); }, [gameOver, level]);

  const answer = (opt) => {
    if (feedback) return;
    const correct = opt === current.ans;
    setFeedback({ correct, selected: opt });
    if (correct) {
      const newStreak = streak + 1;
      setStreak(newStreak);
      setScore(s => s + 10 + (newStreak > 2 ? 5 * (newStreak - 2) : 0));
      if (newStreak > bestStreak) { setBestStreak(newStreak); localStorage.setItem("math_best", newStreak); }
      if (newStreak % 5 === 0) setLevel(l => Math.min(l + 1, 5));
      setTimeout(() => gen(), 700);
    } else {
      setStreak(0);
      const newLives = lives - 1;
      setLives(newLives);
      if (newLives <= 0) { setTimeout(() => setGameOver(true), 700); }
      else setTimeout(() => gen(), 900);
    }
  };

  const restart = () => { setScore(0); setLives(3); setStreak(0); setLevel(1); setGameOver(false); setFeedback(null); };

  if (gameOver) return (
    <div style={{ textAlign: "center", padding: "32px 20px" }}>
      <div style={{ fontSize: 56, marginBottom: 12 }}>💀</div>
      <div style={{ fontSize: 24, fontWeight: 800, color: C.text, marginBottom: 6 }}>Game Over!</div>
      <div style={{ fontSize: 36, fontWeight: 900, color: C.goldDark, marginBottom: 4 }}>{score} pts</div>
      <div style={{ fontSize: 14, color: C.textMuted, marginBottom: 16 }}>Best streak: {bestStreak}</div>
      <button style={S.btn("primary", "lg")} onClick={restart}>Play Again</button>
    </div>
  );

  return (
    <div style={{ maxWidth: 420, margin: "0 auto" }}>
      <div style={{ display: "flex", justify: "space-between", alignItems: "center", marginBottom: 20 }}>
        <div style={{ display: "flex", gap: 16, flex: 1 }}>
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 22, fontWeight: 800, color: C.goldDark }}>{score}</div>
            <div style={{ fontSize: 10, color: C.textMuted }}>SCORE</div>
          </div>
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 22, fontWeight: 800, color: "#F43F5E" }}>{"❤️".repeat(lives)}{"🖤".repeat(3 - lives)}</div>
            <div style={{ fontSize: 10, color: C.textMuted }}>LIVES</div>
          </div>
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 22, fontWeight: 800, color: "#10B981" }}>{streak} 🔥</div>
            <div style={{ fontSize: 10, color: C.textMuted }}>STREAK</div>
          </div>
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 22, fontWeight: 800, color: "#8B5CF6" }}>Lv.{level}</div>
            <div style={{ fontSize: 10, color: C.textMuted }}>LEVEL</div>
          </div>
        </div>
      </div>
      <div style={{ textAlign: "center", margin: "32px 0", padding: "28px", background: C.surfaceAlt, borderRadius: 20, border: `1px solid ${C.border}` }}>
        <div style={{ fontSize: 48, fontWeight: 900, color: C.text, letterSpacing: -2 }}>{current?.q} = ?</div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        {options.map((opt, i) => {
          const isSelected = feedback?.selected === opt;
          const isCorrect = opt === current?.ans;
          const showColor = feedback && (isSelected || isCorrect);
          return (
            <button key={i} onClick={() => answer(opt)} style={{ padding: "18px", borderRadius: 14, border: `2px solid ${showColor ? (isCorrect ? "#6EE7B7" : "#FCA5B1") : C.border}`, background: showColor ? (isCorrect ? C.emeraldLight : C.roseLight) : C.surfaceAlt, color: showColor ? (isCorrect ? "#059669" : "#E11D48") : C.text, fontSize: 24, fontWeight: 800, cursor: "pointer", transition: "all 0.15s", transform: isSelected && feedback ? "scale(0.95)" : "scale(1)", fontFamily: "inherit" }}>
              {opt}
            </button>
          );
        })}
      </div>
      {streak >= 3 && <div style={{ textAlign: "center", marginTop: 14, fontSize: 13, color: C.goldDark }}>🔥 {streak} streak! +{5 * (streak - 2)} bonus per correct!</div>}
    </div>
  );
}

// ── MAIN GAMES PAGE ────────────────────────────────────────────────────────
export default function Games() {
  const [activeGame, setActiveGame] = useState(null);

  const games = [
    { id: "memory", name: "Memory Cards", icon: "🃏", desc: "Match pairs of academic emoji cards. Train your memory!", color: "#3B82F6", gradient: G.blue, difficulty: "Easy" },
    { id: "math", name: "Quick Math", icon: "🧮", desc: "Solve math problems fast! Score combos with streaks.", color: "#F59E0B", gradient: G.gold, difficulty: "Medium" },
  ];

  if (activeGame) {
    const g = games.find(x => x.id === activeGame);
    return (
      <div>
        <button onClick={() => setActiveGame(null)} style={{ ...S.btn("ghost", "sm"), marginBottom: 24, color: C.navy }}>← Back to Games</button>
        <div style={{ textAlign: "center", marginBottom: 28 }}>
          <div style={{ width: 64, height: 64, background: g.gradient, borderRadius: 18, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 32, margin: "0 auto 12px", boxShadow: `0 6px 16px ${g.color}33` }}>{g.icon}</div>
          <h2 style={{ fontSize: 22, fontWeight: 800, color: C.text, margin: "0 0 4px" }}>{g.name}</h2>
          <p style={{ fontSize: 13, color: C.textMuted, margin: 0 }}>{g.desc}</p>
        </div>
        {activeGame === "memory" && <MemoryGame />}
        {activeGame === "math" && <MathQuiz />}
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: 28 }}>
        <h2 style={S.pageTitle}>🎮 Games</h2>
        <p style={S.pageSub}>Take a break and have fun! Train your brain between studies.</p>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(280px,1fr))", gap: 20 }}>
        {games.map(g => (
          <div key={g.id} onClick={() => setActiveGame(g.id)}
            style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 20, padding: "28px 24px", cursor: "pointer", transition: "all 0.25s ease", textAlign: "center", boxShadow: "0 1px 3px rgba(15,23,42,0.06)" }}
            onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-4px)"; e.currentTarget.style.boxShadow = `0 10px 24px ${g.color}26`; e.currentTarget.style.borderColor = g.color + "66"; }}
            onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 1px 3px rgba(15,23,42,0.06)"; e.currentTarget.style.borderColor = C.border; }}>
            <div style={{ width: 72, height: 72, background: g.gradient, borderRadius: 20, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 36, margin: "0 auto 18px", boxShadow: `0 6px 16px ${g.color}33` }}>{g.icon}</div>
            <h3 style={{ fontSize: 18, fontWeight: 800, color: C.text, margin: "0 0 8px" }}>{g.name}</h3>
            <p style={{ fontSize: 13, color: C.textMuted, margin: "0 0 16px", lineHeight: 1.6 }}>{g.desc}</p>
            <Badge type={g.difficulty === "Easy" ? "success" : "warning"}>{g.difficulty}</Badge>
            <div style={{ marginTop: 18, background: g.gradient, color: C.white, padding: "10px 24px", borderRadius: 10, fontSize: 14, fontWeight: 700, display: "inline-block", boxShadow: `0 4px 12px ${g.color}33` }}>Play Now →</div>
          </div>
        ))}
      </div>
      <div style={{ marginTop: 32, padding: "20px 24px", background: C.surfaceAlt, borderRadius: 16, border: `1px solid ${C.border}`, display: "flex", alignItems: "center", gap: 16 }}>
        <span style={{ fontSize: 28 }}>💡</span>
        <div>
          <div style={{ fontWeight: 700, color: C.text, fontSize: 14, marginBottom: 4 }}>Study Tip</div>
          <div style={{ fontSize: 13, color: C.textMuted }}>Taking short game breaks between study sessions improves memory retention and focus. Play for 5-10 minutes, then get back to your courses!</div>
        </div>
      </div>
    </div>
  );
}
